<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Alert Management</h1>
        <p class="text-gray-500 mt-1">Monitor and respond to important notifications</p>
      </div>
      <button
        @click="showSettings = true"
        class="btn btn-secondary flex items-center space-x-2"
      >
        <CogIcon class="w-5 h-5" />
        <span>Alert Settings</span>
      </button>
    </div>
    
    <!-- Stats -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">Total Alerts</p>
              <p class="text-2xl font-bold">{{ alertStats.total }}</p>
            </div>
            <BellIcon class="w-8 h-8 text-gray-400" />
          </div>
        </div>
      </div>
      
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">Active</p>
              <p class="text-2xl font-bold text-warning-600">{{ alertStats.active }}</p>
            </div>
            <ExclamationTriangleIcon class="w-8 h-8 text-warning-500" />
          </div>
        </div>
      </div>
      
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">Critical</p>
              <p class="text-2xl font-bold text-danger-600">{{ alertStats.critical }}</p>
            </div>
            <ExclamationCircleIcon class="w-8 h-8 text-danger-500" />
          </div>
        </div>
      </div>
      
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">Acknowledged</p>
              <p class="text-2xl font-bold text-info-600">{{ alertStats.acknowledged }}</p>
            </div>
            <CheckCircleIcon class="w-8 h-8 text-info-500" />
          </div>
        </div>
      </div>
      
      <div class="card">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500">Resolved</p>
              <p class="text-2xl font-bold text-success-600">{{ alertStats.resolved }}</p>
            </div>
            <CheckBadgeIcon class="w-8 h-8 text-success-500" />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Filters -->
    <div class="card">
      <div class="card-body">
        <div class="flex flex-wrap gap-4">
          <div class="flex-1 min-w-[200px]">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search alerts..."
              class="input w-full"
            />
          </div>
          <select v-model="filters.status" class="input">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
          <select v-model="filters.severity" class="input">
            <option value="">All Severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select v-model="filters.type" class="input">
            <option value="">All Types</option>
            <option value="health">Health</option>
            <option value="medication">Medication</option>
            <option value="wellness">Wellness</option>
            <option value="fall">Fall Risk</option>
            <option value="social">Social</option>
          </select>
          <select v-model="filters.profileId" class="input">
            <option :value="null">All Profiles</option>
            <option
              v-for="profile in profiles"
              :key="profile.id"
              :value="profile.id"
            >
              {{ profile.name }}
            </option>
          </select>
          <button
            @click="resetFilters"
            class="btn btn-secondary"
          >
            Reset
          </button>
        </div>
        
        <!-- Bulk Actions -->
        <div v-if="selectedAlerts.length > 0" class="flex items-center gap-3 mt-4 p-3 bg-primary-50 rounded">
          <span class="text-sm font-medium">{{ selectedAlerts.length }} selected</span>
          <button
            @click="bulkAcknowledge"
            class="btn btn-sm btn-primary"
          >
            Acknowledge All
          </button>
          <button
            @click="bulkDismiss"
            class="btn btn-sm btn-secondary"
          >
            Dismiss All
          </button>
          <button
            @click="selectedAlerts = []"
            class="btn btn-sm btn-secondary"
          >
            Clear Selection
          </button>
        </div>
      </div>
    </div>
    
    <!-- Alert Tabs -->
    <div class="card">
      <div class="card-header">
        <div class="flex space-x-6 border-b">
          <button
            v-for="tab in ['all', 'critical', 'active', 'resolved']"
            :key="tab"
            @click="activeTab = tab"
            :class="[
              'pb-3 px-1 border-b-2 transition-colors',
              activeTab === tab
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            ]"
          >
            {{ tab.charAt(0).toUpperCase() + tab.slice(1) }}
            <span class="ml-2 badge badge-sm">
              {{ getTabCount(tab) }}
            </span>
          </button>
        </div>
      </div>
      
      <div class="card-body p-0">
        <div v-if="loading" class="flex justify-center py-12">
          <div class="spinner"></div>
        </div>
        
        <div v-else-if="filteredAlerts.length === 0" class="text-center py-12">
          <BellSlashIcon class="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p class="text-gray-500">No alerts found</p>
        </div>
        
        <div v-else class="divide-y divide-gray-200">
          <div
            v-for="alert in paginatedAlerts"
            :key="alert.id"
            class="p-4 hover:bg-gray-50"
          >
            <div class="flex items-start">
              <!-- Checkbox -->
              <input
                type="checkbox"
                :value="alert.id"
                v-model="selectedAlerts"
                class="mt-1 mr-3"
              />
              
              <!-- Icon -->
              <div
                :class="[
                  'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mr-3',
                  getSeverityColor(alert.severity)
                ]"
              >
                <component :is="getAlertIcon(alert.type)" class="w-5 h-5 text-white" />
              </div>
              
              <!-- Content -->
              <div class="flex-1">
                <div class="flex items-start justify-between">
                  <div>
                    <h4 class="font-medium text-gray-900">{{ alert.title }}</h4>
                    <p class="text-sm text-gray-600 mt-1">{{ alert.message }}</p>
                    <div class="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span class="flex items-center">
                        <UserIcon class="w-3 h-3 mr-1" />
                        {{ alert.profile?.name || 'Unknown' }}
                      </span>
                      <span class="flex items-center">
                        <ClockIcon class="w-3 h-3 mr-1" />
                        {{ formatTime(alert.created_at) }}
                      </span>
                      <span :class="['badge badge-sm', getSeverityBadgeClass(alert.severity)]">
                        {{ alert.severity }}
                      </span>
                      <span :class="['badge badge-sm', getStatusBadgeClass(alert.status)]">
                        {{ alert.status }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Actions -->
                  <div class="flex space-x-2">
                    <button
                      v-if="alert.status === 'active'"
                      @click="acknowledgeAlert(alert)"
                      class="btn btn-sm btn-primary"
                      title="Acknowledge"
                    >
                      <CheckIcon class="w-4 h-4" />
                    </button>
                    <button
                      v-if="alert.status !== 'resolved'"
                      @click="resolveAlert(alert)"
                      class="btn btn-sm btn-success"
                      title="Resolve"
                    >
                      <CheckCircleIcon class="w-4 h-4" />
                    </button>
                    <button
                      @click="viewDetails(alert)"
                      class="btn btn-sm btn-secondary"
                      title="View Details"
                    >
                      <EyeIcon class="w-4 h-4" />
                    </button>
                    <button
                      v-if="alert.status === 'active'"
                      @click="dismissAlert(alert)"
                      class="btn btn-sm btn-secondary"
                      title="Dismiss"
                    >
                      <XMarkIcon class="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Pagination -->
        <div v-if="totalPages > 1" class="px-6 py-4 border-t">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-700">
              Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to 
              {{ Math.min(currentPage * itemsPerPage, filteredAlerts.length) }} of 
              {{ filteredAlerts.length }} alerts
            </div>
            <div class="flex space-x-2">
              <button
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="btn btn-secondary btn-sm"
              >
                Previous
              </button>
              <button
                @click="currentPage++"
                :disabled="currentPage === totalPages"
                class="btn btn-secondary btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Alert Details Modal -->
    <AlertDetailsModal
      v-if="selectedAlert"
      :alert="selectedAlert"
      @acknowledge="acknowledgeAlert"
      @resolve="resolveAlert"
      @dismiss="dismissAlert"
      @close="selectedAlert = null"
    />
    
    <!-- Alert Settings Modal -->
    <AlertSettingsModal
      v-if="showSettings"
      @save="saveSettings"
      @close="showSettings = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAlertStore } from '@/stores/alerts'
import { useProfileStore } from '@/stores/profiles'
import {
  BellIcon,
  BellSlashIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  CheckBadgeIcon,
  CogIcon,
  UserIcon,
  ClockIcon,
  CheckIcon,
  EyeIcon,
  XMarkIcon,
  HeartIcon,
  BeakerIcon,
  UserGroupIcon
} from '@heroicons/vue/24/outline'
import AlertDetailsModal from '@/components/alerts/AlertDetailsModal.vue'
import AlertSettingsModal from '@/components/alerts/AlertSettingsModal.vue'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

const alertStore = useAlertStore()
const profileStore = useProfileStore()

const loading = computed(() => alertStore.loading)
const alerts = computed(() => alertStore.alerts)
const alertStats = computed(() => alertStore.alertStats)
const profiles = computed(() => profileStore.profiles)

const searchQuery = ref('')
const filters = ref({
  status: '',
  severity: '',
  type: '',
  profileId: null
})
const activeTab = ref('all')
const selectedAlerts = ref([])
const currentPage = ref(1)
const itemsPerPage = 10
const selectedAlert = ref(null)
const showSettings = ref(false)

const filteredAlerts = computed(() => {
  let result = [...alerts.value]
  
  // Tab filter
  if (activeTab.value === 'critical') {
    result = result.filter(a => a.severity === 'critical')
  } else if (activeTab.value === 'active') {
    result = result.filter(a => a.status === 'active')
  } else if (activeTab.value === 'resolved') {
    result = result.filter(a => a.status === 'resolved')
  }
  
  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(alert =>
      alert.title?.toLowerCase().includes(query) ||
      alert.message?.toLowerCase().includes(query) ||
      alert.profile?.name.toLowerCase().includes(query)
    )
  }
  
  // Status filter
  if (filters.value.status) {
    result = result.filter(a => a.status === filters.value.status)
  }
  
  // Severity filter
  if (filters.value.severity) {
    result = result.filter(a => a.severity === filters.value.severity)
  }
  
  // Type filter
  if (filters.value.type) {
    result = result.filter(a => a.type === filters.value.type)
  }
  
  // Profile filter
  if (filters.value.profileId) {
    result = result.filter(a => a.profile_id === filters.value.profileId)
  }
  
  // Sort by date (newest first)
  result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  
  return result
})

const paginatedAlerts = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage
  return filteredAlerts.value.slice(start, end)
})

const totalPages = computed(() => {
  return Math.ceil(filteredAlerts.value.length / itemsPerPage)
})

function getTabCount(tab) {
  switch (tab) {
    case 'all':
      return alerts.value.length
    case 'critical':
      return alerts.value.filter(a => a.severity === 'critical').length
    case 'active':
      return alertStats.value.active
    case 'resolved':
      return alertStats.value.resolved
    default:
      return 0
  }
}

function getAlertIcon(type) {
  const icons = {
    health: HeartIcon,
    medication: BeakerIcon,
    wellness: HeartIcon,
    fall: ExclamationTriangleIcon,
    social: UserGroupIcon
  }
  return icons[type] || BellIcon
}

function getSeverityColor(severity) {
  const colors = {
    critical: 'bg-danger-500',
    high: 'bg-warning-500',
    medium: 'bg-info-500',
    low: 'bg-success-500'
  }
  return colors[severity] || 'bg-gray-500'
}

function getSeverityBadgeClass(severity) {
  const classes = {
    critical: 'badge-danger',
    high: 'badge-warning',
    medium: 'badge-info',
    low: 'badge-success'
  }
  return classes[severity] || 'badge-secondary'
}

function getStatusBadgeClass(status) {
  const classes = {
    active: 'badge-warning',
    acknowledged: 'badge-info',
    resolved: 'badge-success',
    dismissed: 'badge-secondary'
  }
  return classes[status] || 'badge-secondary'
}

function formatTime(datetime) {
  return dayjs(datetime).fromNow()
}

function resetFilters() {
  searchQuery.value = ''
  filters.value = {
    status: '',
    severity: '',
    type: '',
    profileId: null
  }
  currentPage.value = 1
}

async function acknowledgeAlert(alert) {
  await alertStore.acknowledgeAlert(alert.id)
}

async function resolveAlert(alert) {
  const resolution = prompt('Enter resolution notes:')
  if (resolution) {
    await alertStore.resolveAlert(alert.id, resolution)
  }
}

async function dismissAlert(alert) {
  if (confirm('Are you sure you want to dismiss this alert?')) {
    await alertStore.dismissAlert(alert.id)
  }
}

async function bulkAcknowledge() {
  await alertStore.bulkAcknowledge(selectedAlerts.value)
  selectedAlerts.value = []
}

async function bulkDismiss() {
  if (confirm(`Are you sure you want to dismiss ${selectedAlerts.value.length} alerts?`)) {
    for (const alertId of selectedAlerts.value) {
      await alertStore.dismissAlert(alertId)
    }
    selectedAlerts.value = []
  }
}

function viewDetails(alert) {
  selectedAlert.value = alert
}

async function saveSettings(settings) {
  await alertStore.updateAlertSettings(settings)
  showSettings.value = false
}

onMounted(async () => {
  await Promise.all([
    alertStore.fetchAlerts(),
    profileStore.fetchProfiles()
  ])
})
</script>